import * as React from "react"
import type { DialogContentProps } from "@radix-ui/react-dialog"

/**
 * Proteção contra descarte acidental do que foi digitado num diálogo/painel
 * (Radix `Dialog`, base do `DialogContent` e do `SheetContent`).
 *
 * Com texto digitado, o PRIMEIRO Esc e o PRIMEIRO clique fora não fecham:
 * mostram um aviso inline; a segunda tentativa fecha. ✕ e botões
 * Cancelar/Fechar não passam por aqui (são `DialogClose`/`onOpenChange`
 * direto — intenção explícita) e continuam fechando de primeira.
 *
 * De onde vem a marca de "trabalho": do EVENTO `input`, e não de comparar
 * `value` com `defaultValue` — em campo controlado do React os dois andam
 * sempre iguais e a comparação nunca acusa nada. O evento registra QUAIS
 * campos receberam digitação; na hora de fechar, só conta se algum deles
 * ainda está na tela e não está vazio. Assim, apagar tudo o que se digitou,
 * ou enviar a mensagem de um chat (o campo volta a ficar vazio), não deixa
 * uma trava fantasma.
 *
 * O que NÃO conta como trabalho:
 * - campos de busca/filtro: `type="search"`, o campo do `cmdk`
 *   (`[cmdk-input]`, usado pelo `Combobox`) e qualquer coisa dentro de
 *   `[data-no-dirty]` (o `SearchInput` marca o seu campo). Redigitar um
 *   filtro custa nada; travar o Esc por causa dele só irrita.
 * - campos somente leitura, checkbox/radio/select/data: escolha refeita em
 *   um clique, não texto.
 *
 * A marca zera ao fechar. O hook vive no `DialogContent`/`SheetContent`, que a
 * tela mantém montado com o diálogo fechado — quem desmonta é o conteúdo
 * interno do Radix (Presence). Por isso o `DiscardGuardNotice`, que fica DENTRO
 * desse conteúdo, chama `reset` ao desmontar: reabrir começa limpo.
 */

const TEXT_INPUT_TYPES = new Set(["text", "email", "tel", "url", "number", "password"])

function countsAsWork(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) return false
  if (target.closest("[data-no-dirty]")) return false
  if (target instanceof HTMLTextAreaElement) return !target.readOnly
  if (target instanceof HTMLInputElement) {
    if (target.readOnly || target.hasAttribute("cmdk-input")) return false
    return TEXT_INPUT_TYPES.has(target.type)
  }
  return target.isContentEditable
}

function currentText(el: HTMLElement): string {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el.value
  return el.textContent ?? ""
}

type CloseAttempt = "escape" | "outside"

type InteractOutsideEvent = Parameters<NonNullable<DialogContentProps["onInteractOutside"]>>[0]

interface DiscardGuardHandlers {
  onEscapeKeyDown?: (event: KeyboardEvent) => void
  onInteractOutside?: (event: InteractOutsideEvent) => void
  onInput?: React.FormEventHandler<HTMLDivElement>
}

/**
 * Compõe com os handlers que a tela já passa: o da tela roda primeiro e, se
 * ele der `preventDefault`, a decisão dele vale (o diálogo não fecha e esta
 * proteção não se mete).
 */
function useDiscardGuard(screen: DiscardGuardHandlers) {
  // Tudo que decide fica em `ref`: os handlers são chamados pelo Radix fora do
  // ciclo de render, e a decisão não pode depender de um estado congelado.
  // O `useState` serve só para desenhar o aviso.
  const touchedRef = React.useRef(new Set<HTMLElement>())
  const armedRef = React.useRef(false)
  const [notice, setNotice] = React.useState<CloseAttempt | null>(null)

  const hasUnsavedText = () => {
    for (const el of touchedRef.current) {
      if (!el.isConnected) {
        touchedRef.current.delete(el)
        continue
      }
      if (currentText(el).trim() !== "") return true
    }
    return false
  }

  const guard = (event: Event, attempt: CloseAttempt) => {
    if (event.defaultPrevented) return
    // Segunda tentativa (por qualquer caminho) depois do aviso: descarta.
    if (armedRef.current) return
    if (!hasUnsavedText()) return
    event.preventDefault()
    armedRef.current = true
    setNotice(attempt)
  }

  const onEscapeKeyDown = (event: KeyboardEvent) => {
    screen.onEscapeKeyDown?.(event)
    // Esc com uma lista suspensa sem portal aberta (Combobox) é para fechar a
    // lista: o modal fica, e o descarte não arma. O Radix trata o Esc no
    // `document`, antes do `onKeyDown` da lista — por isso a checagem é aqui.
    const target = event.target
    if (target instanceof Element && target.closest("[data-dropdown-open]")) {
      event.preventDefault()
      return
    }
    guard(event, "escape")
  }

  const onInteractOutside = (event: InteractOutsideEvent) => {
    screen.onInteractOutside?.(event)
    // Só o ponteiro. Foco saindo do modal já é barrado pelo próprio Radix
    // (chega aqui com `defaultPrevented`); num não-modal, foco indo para outro
    // lugar não é pedido de fechar que mereça aviso.
    if (event.type !== "dismissableLayer.pointerDownOutside") return
    guard(event, "outside")
  }

  const onInput: React.FormEventHandler<HTMLDivElement> = (event) => {
    screen.onInput?.(event)
    if (!countsAsWork(event.target)) return
    touchedRef.current.add(event.target)
    // Voltou a digitar depois do aviso: a pessoa escolheu continuar. O próximo
    // Esc/clique fora avisa de novo em vez de descartar.
    if (armedRef.current) {
      armedRef.current = false
      setNotice(null)
    }
  }

  const reset = React.useCallback(() => {
    touchedRef.current.clear()
    armedRef.current = false
    setNotice(null)
  }, [])

  return { onEscapeKeyDown, onInteractOutside, onInput, notice, reset }
}

const NOTICE_TEXT: Record<CloseAttempt, string> = {
  escape: "Há texto digitado. Pressione Esc de novo para descartar.",
  outside: "Há texto digitado. Clique fora de novo para descartar.",
}

/**
 * Faixa discreta, dentro do próprio diálogo (nada de modal por cima).
 * `sticky bottom-0`: quando o conteúdo do diálogo rola, o aviso fica à vista
 * mesmo com a pessoa no topo do formulário.
 */
function DiscardGuardNotice({ guard }: { guard: ReturnType<typeof useDiscardGuard> }) {
  const { notice: attempt, reset } = guard
  // Desmonta junto com o conteúdo do Radix ao fechar: zera a marca.
  React.useEffect(() => reset, [reset])
  if (!attempt) return null
  return (
    <div
      role="status"
      data-discard-guard-notice=""
      className="sticky bottom-0 z-10 shrink-0 rounded-md border border-yellow-500/50 bg-card px-3 py-2 text-sm text-yellow-700 shadow-sm dark:text-yellow-400"
    >
      {NOTICE_TEXT[attempt]}
    </div>
  )
}

export { useDiscardGuard, DiscardGuardNotice }
